import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

/**
 * Function type for getting access token asynchronously.
 * Returns null if token is not available.
 */
export type GetAccessToken = () => Promise<string | null>

/**
 * Singleton access token getter function.
 * This is set by the auth provider (e.g., KindeProvider via TokenInterceptorSetup).
 */
let getAccessToken: GetAccessToken | null = null

/**
 * Sets the access token getter function.
 * This should be called once during app initialization by the auth provider.
 *
 * @example
 * ```tsx
 * // Inside KindeProvider
 * function TokenInterceptorSetup() {
 *   const { getAccessToken } = useKindeAuth()
 *
 *   useEffect(() => {
 *     setAccessTokenGetter(async () => {
 *       const token = await getAccessToken()
 *       return token ?? null
 *     })
 *
 *     return () => {
 *       setAccessTokenGetter(() => Promise.resolve(null))
 *     }
 *   }, [getAccessToken])
 *
 *   return null
 * }
 * ```
 */
export function setAccessTokenGetter(getter: GetAccessToken) {
  getAccessToken = getter
}

/**
 * Request interceptor that automatically adds Authorization header.
 * Skips if Authorization header is already set (allows withoutAuth() pattern).
 */
async function requestInterceptor(config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> {
  // If Authorization header is already set, respect it (allows withoutAuth())
  if ('Authorization' in config.headers) {
    return config
  }

  // Get access token if getter is available
  if (getAccessToken) {
    try {
      const token = await getAccessToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.warn('Failed to get access token:', error)
    }
  }

  return config
}

/**
 * Response interceptor that handles 401 errors.
 * Redirects to sign-in page on authentication failure.
 */
function responseErrorInterceptor(error: unknown) {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = error.response as { status?: number }
    if (response.status === 401) {
      // Redirect to sign-in page on 401
      window.location.replace('/sign-in')
    }
  }
  return Promise.reject(error)
}

/**
 * Attaches request and response interceptors to an Axios instance.
 * Should be called once during axios instance initialization.
 *
 * @param instance - The Axios instance to attach interceptors to
 */
export function attachInterceptors(instance: AxiosInstance) {
  instance.interceptors.request.use(requestInterceptor)
  instance.interceptors.response.use(
    (response) => response,
    responseErrorInterceptor,
  )
}
